
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from '@/components/ui/link-compat';
import { useRouter } from '@/lib/router-compat';
import { Eye, EyeOff, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ApiError } from '@/lib/api/client';
import { PasswordRequirements } from '@/components/ui/password-requirements';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { organizationApi } from '@/lib/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { AuthShell, AuthCard } from '@/components/auth/auth-shell';

const registerSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/\d/, 'Password must contain a number'),
  gdprConsent: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Terms of Service and acknowledge the Privacy Policy to continue.',
  }),
});

type RegisterFormData = z.infer<typeof registerSchema>;

/** Map ASP.NET Identity error descriptions to user-friendly messages. */
function mapIdentityErrors(errors: string[]): { field: 'email' | 'password' | null; message: string }[] {
  return errors.map((e) => {
    const lower = e.toLowerCase();
    if (lower.includes('already taken') || lower.includes('duplicate'))
      return { field: 'email', message: 'An account with this email already exists.' };
    if (lower.includes('password') && lower.includes('digit'))
      return { field: 'password', message: 'Password must contain at least one number.' };
    if (lower.includes('password') && lower.includes('length'))
      return { field: 'password', message: 'Password is too short.' };
    if (lower.includes('password'))
      return { field: 'password', message: e };
    return { field: null, message: e };
  });
}

function RegisterPage() {
  usePageTitle('Create Account');
  const router = useRouter();
  const { register: registerUser, loadOrganizations, switchOrganization, token, isLoading: authLoading } = useAuth();
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);

  useEffect(() => {
    if (!authLoading && token && !showOnboarding) {
      router.replace('/dashboard');
    }
  }, [authLoading, token, router, showOnboarding]);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { gdprConsent: false },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setFormError('');
      setIsLoading(true);
      await registerUser({ email: data.email, password: data.password });
      setShowOnboarding(true);
    } catch (err) {
      if (err instanceof ApiError) {
        // Parse structured Identity errors from { errors: string[] }
        const raw = err.details as Record<string, unknown> | null;
        const errorsArray = raw && Array.isArray(raw.errors) ? (raw.errors as string[]) : null;

        if (errorsArray && errorsArray.length > 0) {
          const mapped = mapIdentityErrors(errorsArray);
          let hasFieldError = false;
          for (const m of mapped) {
            if (m.field === 'email') {
              setError('email', { message: m.message });
              hasFieldError = true;
            } else if (m.field === 'password') {
              setError('password', { message: m.message });
              hasFieldError = true;
            }
          }
          if (!hasFieldError) {
            // Fallback: show first unmapped error as banner
            setFormError(mapped[0].message);
          }
        } else if (err.status === 429) {
          setFormError('Too many registration attempts. Please wait a moment and try again.');
        } else if (err.status === 0 || err.message === 'Failed to fetch') {
          setFormError('Unable to connect. Check your internet connection.');
        } else {
          setFormError(err.message || 'Registration failed. Please try again.');
        }
      } else {
        setFormError('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnboardingComplete = async () => {
    const orgs = await loadOrganizations();
    if (orgs.length === 0) {
      throw new Error('No organization found. Please try logging in again.');
    }
    await switchOrganization(orgs[0].id);
    router.push('/dashboard');
  };

  return (
    <AuthShell variant="compact">
      <AuthCard title="Create Account" subtitle="Sign up for a new LeanGrid account">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {formError && (
            <div className="flex items-start gap-2.5 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="new-password"
                aria-invalid={!!errors.password}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordRequirements password={watch('password') || ''} />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="gdprConsent"
                checked={gdprConsent}
                onCheckedChange={(checked) => {
                  const val = checked === true;
                  setGdprConsent(val);
                  setValue('gdprConsent', val, { shouldValidate: true });
                }}
                className="mt-0.5 shrink-0"
                aria-invalid={!!errors.gdprConsent}
              />
              <label htmlFor="gdprConsent" className="text-sm text-muted-foreground leading-snug cursor-pointer select-none">
                I accept the{' '}
                <a
                  href="https://getleangrid.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                  className="inline-flex items-center gap-0.5 text-foreground underline underline-offset-2 hover:text-primary"
                >
                  Terms of Service
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}of GetLeanGrid (which includes the Data Processing Agreement) and acknowledge the{' '}
                <a
                  href="https://getleangrid.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                  className="inline-flex items-center gap-0.5 text-foreground underline underline-offset-2 hover:text-primary"
                >
                  Privacy Policy
                  <ExternalLink className="h-3 w-3" />
                </a>
                . I declare that I am creating this account for purposes directly related to my trade, business, or profession (B2B).
              </label>
            </div>
            {errors.gdprConsent && (
              <p className="text-sm text-destructive">{errors.gdprConsent.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating account…' : 'Sign Up'}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link href="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      </AuthCard>

      <OnboardingModal
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </AuthShell>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/register')({ component: RegisterPage });
